import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Send,
  Mail,
  MessageSquare,
  MessageCircle,
  Users,
  User,
  Search,
  Plus,
  X,
  ChevronDown,
  Paperclip,
  Image,
  Eye,
  FileText,
  Loader2,
  Check,
  Clock,
  AlertCircle,
  Trash2,
  MoreVertical,
  Edit,
  Settings,
  Star,
  Calendar,
  BarChart2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Upload,
  File,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER_OPTIONS = [
  { label: "Nome ospite", value: "{nome_ospite}" },
  { label: "Nome familiare", value: "{nome_familiare}" },
  { label: "Relazione", value: "{relazione}" },
  { label: "Nome struttura", value: "{struttura}" },
  { label: "Data odierna", value: "{data}" },
  { label: "Firma", value: "{firma}" },
];

const STATO_CONFIG = {
  bozza:       { label: "Bozza",       color: "bg-slate-100 text-slate-700", icon: Edit },
  programmata: { label: "Programmata", color: "bg-blue-100 text-blue-700",   icon: Clock },
  in_invio:    { label: "In invio",    color: "bg-yellow-100 text-yellow-700", icon: Loader2 },
  inviata:     { label: "Inviata",     color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  parziale:    { label: "Parziale",    color: "bg-orange-100 text-orange-700", icon: AlertCircle },
  fallita:     { label: "Fallita",     color: "bg-red-100 text-red-700",     icon: XCircle },
  // programmata prima dell'attivazione dello scheduler: non inviata in automatico
  saltata_backlog: { label: "Non inviata (arretrato)", color: "bg-amber-100 text-amber-800", icon: AlertCircle },
};

// ─────────────────────────────────────────────────────────────
// Componente principale
// ─────────────────────────────────────────────────────────────
export default function Comunicazioni() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("nuova");

  const authHeader = { Authorization: `Bearer ${token}` };

  return (
    <div className="h-full flex flex-col p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Comunicazioni ai parenti"
        subtitle="Crea e invia comunicazioni tramite email o SMS"
        icon={Send}
        iconContainerClassName="bg-green-100"
        iconClassName="text-green-600"
      />

      {/* Tab navigazione */}
      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto shrink-0">
        {[
          { id: "nuova", label: "Nuova comunicazione", icon: Plus },
          { id: "inviate", label: "Comunicazioni inviate", icon: Send },
          { id: "modelli", label: "Modelli", icon: FileText },
          { id: "impostazioni", label: "Impostazioni", icon: Settings },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === id
                ? "border-green-600 text-green-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0">
        {activeTab === "nuova" && (
          <NuovaComunicazione token={token} authHeader={authHeader} />
        )}
        {activeTab === "inviate" && (
          <ComunicazioniInviate token={token} authHeader={authHeader} />
        )}
        {activeTab === "modelli" && (
          <GestioneModelli token={token} authHeader={authHeader} />
        )}
        {activeTab === "impostazioni" && (
          <ImpostazioniComunicazioni token={token} authHeader={authHeader} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Tab: Nuova comunicazione
// ─────────────────────────────────────────────────────────────
function NuovaComunicazione({ token, authHeader }) {
  const { activeStruttura } = useAuth();
  // Stato form
  const [destinatariTab, setDestinatariTab] = useState("singolo");
  const [canale, setCanale] = useState("email");
  const [oggetto, setOggetto] = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [allegati, setAllegati] = useState([]);
  const [opzioneInvio, setOpzioneInvio] = useState("subito");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("10:00");
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewOggetto, setPreviewOggetto] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  // Destinatari selezionati (array di oggetti destinatario)
  const [destinatariSelezionati, setDestinatariSelezionati] = useState([]);

  // Stats
  const [stats, setStats] = useState(null);

  // Modelli rapidi
  const [modelliRapidi, setModelliRapidi] = useState([]);

  // Allegati dialog
  const [allegatiDialogOpen, setAllegatiDialogOpen] = useState(false);

  const textareaRef = useRef(null);
  const authorization = authHeader?.Authorization;

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/comunicazioni/stats`, {
        headers: { Authorization: authorization },
      });
      setStats(data);
    } catch {
      // silente
    }
  }, [authorization]);

  const loadModelliRapidi = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/comunicazioni/modelli`, {
        headers: { Authorization: authorization },
      });
      setModelliRapidi(data.slice(0, 3));
    } catch {
      // silente
    }
  }, [authorization]);

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const dest = destinatariSelezionati[0] || null;
      const { data } = await axios.post(
        `${API_URL}/api/comunicazioni/preview`,
        {
          oggetto,
          messaggio,
          destinatario: dest ? {
            fascicolo_id: dest.fascicolo_id,
            ospite_nome: dest.ospite_nome,
            familiare_nome: dest.familiare_nome,
            familiare_relazione: dest.familiare_relazione,
            email: dest.email,
          } : null,
        },
        { headers: { Authorization: authorization } }
      );
      setPreviewHtml(data.html);
      setPreviewOggetto(data.oggetto);
    } catch {
      // silente
    } finally {
      setPreviewLoading(false);
    }
  }, [authorization, oggetto, messaggio, destinatariSelezionati]);

  useEffect(() => {
    loadStats();
    loadModelliRapidi();
  }, [activeStruttura?.id, loadStats, loadModelliRapidi]);

  // Preview auto-refresh quando cambia contenuto
  useEffect(() => {
    const t = setTimeout(() => {
      if (messaggio) fetchPreview();
    }, 600);
    return () => clearTimeout(t);
  }, [messaggio, oggetto, destinatariSelezionati, fetchPreview]);

  function inserisciPlaceholder(placeholder) {
    const ta = textareaRef.current;
    if (!ta) {
      setMessaggio((m) => m + placeholder);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newVal = messaggio.slice(0, start) + placeholder + messaggio.slice(end);
    setMessaggio(newVal);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  }

  function applicaModello(modello) {
    setOggetto(modello.oggetto || "");
    setMessaggio(modello.messaggio || "");
    toast.success(`Modello "${modello.nome}" applicato`);
  }

  async function handleInvia(action) {
    if (destinatariSelezionati.length === 0) {
      toast.error("Seleziona almeno un destinatario");
      return;
    }
    if (canale === "email" && !oggetto.trim()) {
      toast.error("Inserisci l'oggetto del messaggio");
      return;
    }
    if (!messaggio.trim()) {
      toast.error("Inserisci il testo del messaggio");
      return;
    }
    if (action === "schedule" && !scheduledDate) {
      toast.error("Seleziona data e ora per l'invio programmato");
      return;
    }

    setSending(true);
    try {
      let scheduled_at = null;
      if (action === "schedule") {
        scheduled_at = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
      }

      const payload = {
        canale,
        oggetto: oggetto || null,
        messaggio,
        destinatari: destinatariSelezionati,
        allegati: allegati.map((a) => ({
          id: a.id,
          nome: a.nome,
          size: a.size,
          mime: a.mime,
          source: a.source,
          file_path: a.file_path,
        })),
        scheduled_at,
        action,
      };

      await axios.post(`${API_URL}/api/comunicazioni/`, payload, { headers: authHeader });

      const msg = action === "bozza"
        ? "Bozza salvata"
        : action === "schedule"
        ? "Invio programmato con successo"
        : `Invio avviato verso ${destinatariSelezionati.length} destinatario/i`;
      toast.success(msg);

      // Reset form
      setDestinatariSelezionati([]);
      setOggetto("");
      setMessaggio("");
      setAllegati([]);
      setPreviewHtml("");
      setPreviewOggetto("");
      setOpzioneInvio("subito");
      setScheduledDate("");
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_440px] gap-6 pb-6">
      {/* ── Colonna sinistra ── */}
      <div className="space-y-4">
        {/* Step 1: Destinatari */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                1
              </div>
              <h3 className="font-semibold text-slate-800">Seleziona destinatari</h3>
            </div>

            {/* Sub-tab destinatari */}
            <div className="flex gap-1 mb-4 border rounded-lg p-1 bg-slate-50">
              {[
                { id: "singolo", label: "Singolo destinatario" },
                { id: "multiplo", label: "Più destinatari" },
                { id: "gruppi", label: "Gruppi" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setDestinatariTab(id)}
                  className={`flex-1 py-1.5 px-2 text-xs font-medium rounded-md transition-colors ${
                    destinatariTab === id
                      ? "bg-white shadow-sm text-slate-800"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {destinatariTab === "singolo" && (
              <SingoloDestinatario
                authHeader={authHeader}
                selezionati={destinatariSelezionati}
                onSeleziona={(d) => setDestinatariSelezionati([d])}
                onRimuovi={() => setDestinatariSelezionati([])}
              />
            )}
            {destinatariTab === "multiplo" && (
              <MultiDestinatario
                authHeader={authHeader}
                selezionati={destinatariSelezionati}
                onAggiorna={setDestinatariSelezionati}
              />
            )}
            {destinatariTab === "gruppi" && (
              <GruppiDestinatario
                authHeader={authHeader}
                selezionati={destinatariSelezionati}
                onSeleziona={setDestinatariSelezionati}
              />
            )}

            {destinatariSelezionati.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {destinatariSelezionati.map((d, i) => (
                  <div
                    key={`${d.fascicolo_id}-${d.familiare_nome}-${i}`}
                    className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1 text-xs"
                  >
                    <User className="w-3 h-3 text-green-600" />
                    <span className="text-green-800 font-medium">{d.familiare_nome}</span>
                    <span className="text-green-600">({d.ospite_nome})</span>
                    <button
                      onClick={() =>
                        setDestinatariSelezionati((prev) =>
                          prev.filter((_, idx) => idx !== i)
                        )
                      }
                      className="ml-1 text-green-500 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Canale */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                2
              </div>
              <h3 className="font-semibold text-slate-800">Scegli il canale</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Email */}
              <button
                onClick={() => setCanale("email")}
                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                  canale === "email"
                    ? "border-green-500 bg-green-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                {canale === "email" && (
                  <Badge className="absolute top-2 right-2 bg-green-600 text-white text-[10px] py-0">
                    Consigliato
                  </Badge>
                )}
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-2">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <p className="font-semibold text-sm text-slate-800">Email</p>
                <p className="text-xs text-slate-500 mt-0.5">Invia comunicazione via email</p>
              </button>

              {/* SMS - Prossimamente */}
              <div className="relative p-4 rounded-xl border-2 border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed">
                <Badge className="absolute top-2 right-2 bg-slate-400 text-white text-[10px] py-0">
                  Prossimamente
                </Badge>
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                  <MessageSquare className="w-5 h-5 text-slate-400" />
                </div>
                <p className="font-semibold text-sm text-slate-500">SMS</p>
                <p className="text-xs text-slate-400 mt-0.5">Invia comunicazione via SMS</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Messaggio */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                3
              </div>
              <h3 className="font-semibold text-slate-800">Componi il messaggio</h3>
            </div>

            {canale === "email" && (
              <div className="mb-3">
                <Label className="text-xs text-slate-500 mb-1 block">Oggetto (solo email)</Label>
                <Input
                  placeholder="Inserisci l'oggetto della comunicazione..."
                  value={oggetto}
                  onChange={(e) => setOggetto(e.target.value)}
                  maxLength={300}
                />
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">
                Messaggio <span className="text-red-500">*</span>
              </Label>
              <Textarea
                ref={textareaRef}
                placeholder="Scrivi qui il tuo messaggio..."
                value={messaggio}
                onChange={(e) => setMessaggio(e.target.value)}
                className="min-h-[160px] resize-none"
                maxLength={10000}
              />
              <p className="text-right text-xs text-slate-400 mt-1">
                {messaggio.length}/10000
              </p>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {/* Inserisci campo */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8">
                    <Plus className="w-3 h-3" />
                    Inserisci campo
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {PLACEHOLDER_OPTIONS.map((p) => (
                    <DropdownMenuItem
                      key={p.value}
                      onClick={() => inserisciPlaceholder(p.value)}
                    >
                      {p.label}
                      <span className="ml-2 font-mono text-xs text-slate-400">{p.value}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Allegati (solo email) */}
              {canale === "email" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs h-8"
                  onClick={() => setAllegatiDialogOpen(true)}
                >
                  <Paperclip className="w-3 h-3" />
                  Allega file
                  {allegati.length > 0 && (
                    <Badge className="ml-1 bg-green-600 text-white text-[10px] py-0 px-1.5 rounded-full">
                      {allegati.length}
                    </Badge>
                  )}
                </Button>
              )}
            </div>

            {/* Lista allegati */}
            {allegati.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {allegati.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5 text-xs"
                  >
                    <File className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="flex-1 text-slate-700 truncate">{a.nome}</span>
                    <span className="text-slate-400">
                      {a.size ? `${Math.round(a.size / 1024)} KB` : ""}
                    </span>
                    <button
                      onClick={() => setAllegati((prev) => prev.filter((x) => x.id !== a.id))}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer azioni */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => handleInvia("bozza")}
            disabled={sending}
            className="gap-2"
          >
            <Edit className="w-4 h-4" />
            Salva bozza
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchPreview}
              disabled={!messaggio || previewLoading}
              className="gap-2"
            >
              <Eye className="w-4 h-4" />
              Anteprima
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white gap-2"
                  disabled={sending || !messaggio}
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Invia comunicazione
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleInvia("send")}>
                  <Send className="w-4 h-4 mr-2" /> Invia subito
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  setOpzioneInvio("schedule");
                  handleInvia("schedule");
                }}>
                  <Clock className="w-4 h-4 mr-2" /> Programma invio
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleInvia("bozza")}>
                  <Edit className="w-4 h-4 mr-2" /> Salva come bozza
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* ── Colonna destra ── */}
      <div className="space-y-4">
        {/* Anteprima */}
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-slate-700">Anteprima messaggio</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {previewLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : previewHtml ? (
              <div>
                {previewOggetto && (
                  <p className="text-xs text-slate-500 mb-2">
                    <span className="font-medium">A:</span>{" "}
                    {destinatariSelezionati[0]?.email || "—"}
                    <br />
                    <span className="font-medium">Oggetto:</span> {previewOggetto}
                  </p>
                )}
                <div
                  className="keep-light border rounded-lg p-3 bg-white text-xs overflow-auto max-h-64"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-28 text-slate-400">
                <Eye className="w-8 h-8 mb-2 opacity-40" />
                <p className="text-xs">Scrivi un messaggio per vedere l'anteprima</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statistiche */}
        {stats && (
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-slate-700">
                Statistiche invio
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Mail className="w-3.5 h-3.5 text-green-600" />
                    <span className="text-lg font-bold text-slate-800">
                      {stats.email_inviate_mese ?? 0}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">Email inviate</p>
                  <p className="text-[10px] text-slate-400">Questo mese</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-lg font-bold text-slate-800">
                      {stats.sms_inviati_mese ?? 0}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">SMS inviati</p>
                  <p className="text-[10px] text-slate-400">Questo mese</p>
                </div>
                <div className="col-span-2 bg-green-50 rounded-lg p-2">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                    <span className="text-lg font-bold text-green-700">
                      {stats.tasso_consegna_mese ?? 0}%
                    </span>
                  </div>
                  <p className="text-[10px] text-green-600 font-medium">Tasso di consegna</p>
                  <p className="text-[10px] text-green-500">Questo mese</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Opzioni di invio */}
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-slate-700">Opzioni di invio</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <RadioGroup value={opzioneInvio} onValueChange={setOpzioneInvio} className="space-y-3">
              <div className="flex items-start gap-2">
                <RadioGroupItem value="subito" id="subito" className="mt-0.5" />
                <div>
                  <Label htmlFor="subito" className="text-sm font-medium cursor-pointer">
                    Invia subito
                  </Label>
                  <p className="text-xs text-slate-500">La comunicazione verrà inviata immediatamente</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="schedule" id="schedule" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="schedule" className="text-sm font-medium cursor-pointer">
                    Programma invio
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">Scegli data e ora per l'invio</p>
                  {opzioneInvio === "schedule" && (
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        className="text-xs h-8"
                        min={new Date().toISOString().split("T")[0]}
                      />
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="text-xs h-8 w-24"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="bozza" id="bozza-radio" className="mt-0.5" />
                <div>
                  <Label htmlFor="bozza-radio" className="text-sm font-medium cursor-pointer">
                    Salva come bozza
                  </Label>
                  <p className="text-xs text-slate-500">Salva la comunicazione per inviarla più tardi</p>
                </div>
              </div>
            </RadioGroup>

            <Button
              className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white gap-2"
              disabled={sending || !messaggio}
              onClick={() => handleInvia(opzioneInvio === "subito" ? "send" : opzioneInvio === "schedule" ? "schedule" : "bozza")}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {opzioneInvio === "subito" ? "Invia ora" : opzioneInvio === "schedule" ? "Programma" : "Salva bozza"}
            </Button>
          </CardContent>
        </Card>

        {/* Modelli rapidi */}
        {modelliRapidi.length > 0 && (
          <Card>
            <CardHeader className="pb-2 pt-4 px-4 flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">Modelli rapidi</CardTitle>
              <span className="text-xs text-green-600 cursor-pointer hover:underline">
                Vedi tutti
              </span>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {modelliRapidi.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-2 p-2 rounded-lg border border-slate-100 hover:border-green-200 hover:bg-green-50 transition-colors"
                >
                  <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{m.nome}</p>
                    {m.ultimo_utilizzo && (
                      <p className="text-[10px] text-slate-400">
                        Ultimo utilizzo: {new Date(m.ultimo_utilizzo).toLocaleDateString("it-IT")}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <MoreVertical className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => applicaModello(m)}>
                        <Check className="w-4 h-4 mr-2" /> Applica
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog allegati */}
      <AllegatiDialog
        open={allegatiDialogOpen}
        onClose={() => setAllegatiDialogOpen(false)}
        authHeader={authHeader}
        allegatiSelezionati={allegati}
        onUpdate={setAllegati}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Singolo destinatario
// ─────────────────────────────────────────────────────────────
function SingoloDestinatario({ authHeader, selezionati, onSeleziona, onRimuovi }) {
  const { activeStruttura } = useAuth();
  const [q, setQ] = useState("");
  const [risultati, setRisultati] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef(null);

  const selezionato = selezionati[0] || null;

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (q.length < 2) {
      setRisultati([]);
      setShowDropdown(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/comunicazioni/destinatari/ricerca`, {
          headers: authHeader,
          params: { q },
        });
        setRisultati(data);
        setShowDropdown(true);
      } catch {
        // silente
      } finally {
        setLoading(false);
      }
    }, 350);
  }, [q, authHeader, activeStruttura?.id]);

  if (selezionato) return null;

  return (
    <div className="relative">
      <Label className="text-xs text-slate-500 mb-1 block">Ospite / Parente *</Label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          className="pl-9"
          placeholder="Cerca ospite o parente..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => risultati.length > 0 && setShowDropdown(true)}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
        )}
      </div>
      {showDropdown && risultati.length > 0 && (
        <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {risultati.map((r, i) => (
            <button
              key={i}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              onClick={() => {
                onSeleziona(r);
                setQ("");
                setShowDropdown(false);
              }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700 shrink-0">
                  {(r.familiare_nome || "?")[0]}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{r.familiare_nome}</p>
                  <p className="text-xs text-slate-500">
                    {r.familiare_relazione} di {r.ospite_nome}
                    {r.email && <span className="ml-1 text-slate-400">· {r.email}</span>}
                  </p>
                </div>
                {!r.ha_email && (
                  <Badge className="ml-auto bg-red-100 text-red-600 text-[10px]">Senza email</Badge>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Multi destinatario
// ─────────────────────────────────────────────────────────────
function MultiDestinatario({ authHeader, selezionati, onAggiorna }) {
  const { activeStruttura } = useAuth();
  const [rubrica, setRubrica] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [espansi, setEspansi] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/comunicazioni/destinatari/rubrica`, {
          headers: authHeader,
        });
        setRubrica(data);
      } catch {
        // silente
      } finally {
        setLoading(false);
      }
    })();
  }, [authHeader, activeStruttura?.id]);

  const rubricaFiltrata = rubrica.filter(
    (o) =>
      !q ||
      o.ospite_nome.toLowerCase().includes(q.toLowerCase()) ||
      o.contatti.some(
        (c) =>
          c.familiare_nome.toLowerCase().includes(q.toLowerCase()) ||
          (c.email || "").toLowerCase().includes(q.toLowerCase())
      )
  );

  function toggleContatto(contatto) {
    const key = `${contatto.fascicolo_id}-${contatto.familiare_nome}`;
    const esiste = selezionati.some(
      (s) => `${s.fascicolo_id}-${s.familiare_nome}` === key
    );
    if (esiste) {
      onAggiorna(selezionati.filter((s) => `${s.fascicolo_id}-${s.familiare_nome}` !== key));
    } else {
      onAggiorna([...selezionati, contatto]);
    }
  }

  function isSelezionato(contatto) {
    return selezionati.some(
      (s) =>
        `${s.fascicolo_id}-${s.familiare_nome}` ===
        `${contatto.fascicolo_id}-${contatto.familiare_nome}`
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          className="pl-9"
          placeholder="Cerca ospite o familiare..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="space-y-2 max-h-72 overflow-auto pr-1">
        {rubricaFiltrata.map((ospite) => (
          <div key={ospite.fascicolo_id} className="border rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700"
              onClick={() =>
                setEspansi((prev) => ({
                  ...prev,
                  [ospite.fascicolo_id]: !prev[ospite.fascicolo_id],
                }))
              }
            >
              <span>{ospite.ospite_nome}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">
                  {ospite.contatti.length} contatti
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-slate-400 transition-transform ${
                    espansi[ospite.fascicolo_id] ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>
            {espansi[ospite.fascicolo_id] && (
              <div className="divide-y">
                {ospite.contatti.map((c, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 px-3 py-2 ${
                      !c.ha_email ? "opacity-50" : ""
                    }`}
                  >
                    <Checkbox
                      id={`c-${ospite.fascicolo_id}-${i}`}
                      checked={isSelezionato(c)}
                      onCheckedChange={() => c.ha_email && toggleContatto(c)}
                      disabled={!c.ha_email}
                    />
                    <label
                      htmlFor={`c-${ospite.fascicolo_id}-${i}`}
                      className={`flex-1 text-sm ${c.ha_email ? "cursor-pointer" : "cursor-not-allowed"}`}
                    >
                      <span className="font-medium text-slate-700">{c.familiare_nome}</span>
                      <span className="text-slate-500 text-xs ml-1">({c.familiare_relazione})</span>
                      {c.email && (
                        <span className="text-slate-400 text-xs block">{c.email}</span>
                      )}
                    </label>
                    {!c.ha_email && (
                      <Badge className="bg-slate-100 text-slate-500 text-[10px]">Senza email</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {rubricaFiltrata.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-4">Nessun risultato</p>
        )}
      </div>
      {selezionati.length > 0 && (
        <p className="text-xs text-green-600 font-medium mt-2">
          {selezionati.length} destinatario/i selezionati
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Gruppi destinatario
// ─────────────────────────────────────────────────────────────
function GruppiDestinatario({ authHeader, selezionati, onSeleziona }) {
  const { activeStruttura } = useAuth();
  const [gruppi, setGruppi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gruppoSelezionato, setGruppoSelezionato] = useState(null);
  const [creaDialogOpen, setCreaDialogOpen] = useState(false);
  const authorization = authHeader?.Authorization;

  const loadGruppi = useCallback(async (selectId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/comunicazioni/gruppi`, {
        headers: { Authorization: authorization },
      });
      const list = Array.isArray(data) ? data : [];
      setGruppi(list);
      if (selectId) {
        const g = list.find((x) => x.id === selectId);
        if (g) {
          setGruppoSelezionato(g.id);
          onSeleziona(g.destinatari || []);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento dei gruppi");
    } finally {
      setLoading(false);
    }
  }, [authorization, onSeleziona]);

  useEffect(() => { loadGruppi(); }, [activeStruttura?.id, loadGruppi]);

  function selezionaGruppo(gruppo) {
    setGruppoSelezionato(gruppo.id);
    onSeleziona(gruppo.destinatari || []);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">Seleziona un gruppo di destinatari salvato</p>
        <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" onClick={() => setCreaDialogOpen(true)}>
          <Plus className="w-3 h-3" /> Crea gruppo
        </Button>
      </div>
      {gruppi.length === 0 ? (
        <div className="text-center py-6 text-slate-400">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Nessun gruppo creato</p>
          <p className="text-xs">Crea un gruppo per inviare rapidamente a più persone</p>
        </div>
      ) : (
        <div className="space-y-2">
          {gruppi.map((g) => (
            <button
              key={g.id}
              onClick={() => selezionaGruppo(g)}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                gruppoSelezionato === g.id
                  ? "border-green-500 bg-green-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{g.nome}</p>
                  <p className="text-xs text-slate-500">
                    {(g.destinatari || []).length} destinatari
                    {g.descrizione && ` · ${g.descrizione}`}
                  </p>
                </div>
                {gruppoSelezionato === g.id && (
                  <Check className="w-4 h-4 text-green-600 ml-auto" />
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      <CreaGruppoDialog
        open={creaDialogOpen}
        onClose={() => setCreaDialogOpen(false)}
        authHeader={authHeader}
        onCreato={(created) => { loadGruppi(created?.id); setCreaDialogOpen(false); }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Dialog crea gruppo
// ─────────────────────────────────────────────────────────────
function CreaGruppoDialog({ open, onClose, authHeader, onCreato }) {
  const { activeStruttura } = useAuth();
  const [nome, setNome] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [rubrica, setRubrica] = useState([]);
  const [selezionati, setSelezionati] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API_URL}/api/comunicazioni/destinatari/rubrica`, { headers: authHeader })
      .then(({ data }) => setRubrica(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, authHeader, activeStruttura?.id]);

  function toggleContatto(c) {
    const key = `${c.fascicolo_id}-${c.familiare_nome}`;
    const esiste = selezionati.some((s) => `${s.fascicolo_id}-${s.familiare_nome}` === key);
    if (esiste) {
      setSelezionati((prev) => prev.filter((s) => `${s.fascicolo_id}-${s.familiare_nome}` !== key));
    } else {
      setSelezionati((prev) => [...prev, c]);
    }
  }

  async function handleSalva() {
    if (!nome.trim()) { toast.error("Inserisci il nome del gruppo"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/comunicazioni/gruppi`, {
        nome, descrizione, destinatari: selezionati,
      }, { headers: authHeader });
      toast.success("Gruppo creato");
      setNome(""); setDescrizione(""); setSelezionati([]);
      onCreato(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Crea nuovo gruppo</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nome gruppo *</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Es. Familiari Camera Nord" />
          </div>
          <div>
            <Label className="text-xs">Descrizione</Label>
            <Input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} placeholder="Descrizione opzionale" />
          </div>
          <div>
            <Label className="text-xs mb-2 block">Seleziona destinatari</Label>
            {loading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : (
              <div className="max-h-52 overflow-auto space-y-1 border rounded-lg p-2">
                {rubrica.map((ospite) =>
                  ospite.contatti.filter((c) => c.ha_email).map((c, i) => {
                    const key = `${c.fascicolo_id}-${c.familiare_nome}`;
                    const checked = selezionati.some((s) => `${s.fascicolo_id}-${s.familiare_nome}` === key);
                    return (
                      <div key={key + i} className="flex items-center gap-2 py-1">
                        <Checkbox checked={checked} onCheckedChange={() => toggleContatto(c)} id={`g-${key}`} />
                        <label htmlFor={`g-${key}`} className="text-xs cursor-pointer">
                          <span className="font-medium">{c.familiare_nome}</span>
                          <span className="text-slate-500"> ({c.familiare_relazione} di {ospite.ospite_nome})</span>
                        </label>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            {selezionati.length > 0 && (
              <p className="text-xs text-green-600 mt-1">{selezionati.length} destinatari selezionati</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleSalva} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva gruppo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Dialog allegati
// ─────────────────────────────────────────────────────────────
function AllegatiDialog({ open, onClose, authHeader, allegatiSelezionati, onUpdate }) {
  const [tabAllegati, setTabAllegati] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (allegatiSelezionati.length >= 5) {
      toast.error("Massimo 5 allegati per comunicazione");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API_URL}/api/comunicazioni/allegati`, formData, {
        headers: { ...authHeader, "Content-Type": "multipart/form-data" },
      });
      onUpdate([...allegatiSelezionati, data]);
      toast.success(`${file.name} aggiunto`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Allega file</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 mb-4 border rounded-lg p-1 bg-slate-50">
          {[
            { id: "upload", label: "Carica nuovo" },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTabAllegati(id)}
              className={`flex-1 py-1.5 px-2 text-xs font-medium rounded-md transition-colors ${
                tabAllegati === id ? "bg-white shadow-sm text-slate-800" : "text-slate-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tabAllegati === "upload" && (
          <div>
            <div
              className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-600 font-medium">Clicca per caricare un file</p>
              <p className="text-xs text-slate-400 mt-1">PDF, Word, Excel, immagini — max 10 MB</p>
              {uploading && <Loader2 className="w-5 h-5 mx-auto mt-2 animate-spin text-green-600" />}
            </div>
            <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} />
          </div>
        )}

        {allegatiSelezionati.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-xs font-medium text-slate-500">File allegati ({allegatiSelezionati.length}/5):</p>
            {allegatiSelezionati.map((a) => (
              <div key={a.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-xs">
                <File className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="flex-1 truncate text-slate-700">{a.nome}</span>
                <button
                  onClick={() => onUpdate(allegatiSelezionati.filter((x) => x.id !== a.id))}
                  className="text-slate-400 hover:text-red-500"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Tab: Comunicazioni inviate
// ─────────────────────────────────────────────────────────────
function ComunicazioniInviate({ authHeader }) {
  const { activeStruttura } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("all");
  const [filtroCanale, setFiltroCanale] = useState("all");
  const [dettaglio, setDettaglio] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  // Comunicazioni non inviate per arretrato: azioni esplicite con conferma
  const [confermaInvio, setConfermaInvio] = useState(null);
  const [riprogramma, setRiprogramma] = useState(null); // { item, data, ora }
  const [riprogrammando, setRiprogrammando] = useState(false);
  const invioInCorso = useRef(false);
  const authorization = authHeader?.Authorization;

  const loadComunicazioni = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStato !== "all") params.stato = filtroStato;
      if (filtroCanale !== "all") params.canale = filtroCanale;
      const { data } = await axios.get(`${API_URL}/api/comunicazioni/`, {
        headers: { Authorization: authorization },
        params,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [authorization, filtroStato, filtroCanale]);

  useEffect(() => { loadComunicazioni(); }, [filtroStato, filtroCanale, activeStruttura?.id, loadComunicazioni]);

  async function handleElimina(id) {
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/api/comunicazioni/${id}`, { headers: authHeader });
      toast.success("Comunicazione eliminata");
      loadComunicazioni();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleInvia(id) {
    if (invioInCorso.current) return; // doppio click: il backend comunque prende in carico una sola volta
    invioInCorso.current = true;
    setSendingId(id);
    try {
      await axios.post(`${API_URL}/api/comunicazioni/${id}/send`, {}, { headers: authHeader });
      toast.success("Invio avviato");
      loadComunicazioni();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio");
    } finally {
      invioInCorso.current = false;
      setSendingId(null);
    }
  }

  async function confermaInvioOra() {
    const item = confermaInvio;
    setConfermaInvio(null);
    if (item) await handleInvia(item.id);
  }

  function apriRiprogramma(item) {
    const domani = new Date(Date.now() + 24 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    setRiprogramma({
      item,
      data: `${domani.getFullYear()}-${pad(domani.getMonth() + 1)}-${pad(domani.getDate())}`,
      ora: "09:00",
    });
  }

  async function salvaRiprogramma() {
    if (!riprogramma || riprogrammando) return;
    const quando = new Date(`${riprogramma.data}T${riprogramma.ora}:00`);
    if (Number.isNaN(quando.getTime()) || quando.getTime() <= Date.now()) {
      toast.error("Scegli una data e un'ora future");
      return;
    }
    setRiprogrammando(true);
    try {
      await axios.post(
        `${API_URL}/api/comunicazioni/${riprogramma.item.id}/riprogramma`,
        { scheduled_at: quando.toISOString() },
        { headers: authHeader },
      );
      toast.success(`Riprogrammata per il ${quando.toLocaleString("it-IT")}`);
      setRiprogramma(null);
      loadComunicazioni();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella riprogrammazione");
    } finally {
      setRiprogrammando(false);
    }
  }

  return (
    <div>
      {/* Filtri */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select value={filtroStato} onValueChange={setFiltroStato}>
          <SelectTrigger className="w-40 h-9 text-sm">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="bozza">Bozze</SelectItem>
            <SelectItem value="programmata">Programmate</SelectItem>
            <SelectItem value="inviata">Inviate</SelectItem>
            <SelectItem value="parziale">Parziali</SelectItem>
            <SelectItem value="fallita">Fallite</SelectItem>
            <SelectItem value="saltata_backlog">Non inviate (arretrato)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filtroCanale} onValueChange={setFiltroCanale}>
          <SelectTrigger className="w-36 h-9 text-sm">
            <SelectValue placeholder="Tutti i canali" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i canali</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={loadComunicazioni}>
          <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
        </Button>
        <span className="text-sm text-slate-500 ml-auto">{total} comunicazioni</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Send className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium text-slate-500">Nessuna comunicazione</p>
          <p className="text-sm">Le comunicazioni inviate appariranno qui</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const config = STATO_CONFIG[item.stato] || STATO_CONFIG.bozza;
            const StatoIcon = config.icon;
            const sentCount = (item.destinatari || []).filter((d) => d.stato_invio === "sent").length;
            const totalDest = (item.destinatari || []).length;

            return (
              <Card key={item.id} className="hover:border-slate-300 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Mail className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-slate-800 text-sm truncate">
                          {item.oggetto || "(senza oggetto)"}
                        </span>
                        <Badge className={`text-[10px] px-2 py-0 ${config.color}`}>
                          <StatoIcon className="w-2.5 h-2.5 mr-1" />
                          {config.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mb-1">
                        {totalDest} destinatario/i
                        {item.stato === "inviata" || item.stato === "parziale"
                          ? ` · ${sentCount}/${totalDest} consegnati`
                          : ""}
                        {item.scheduled_at
                          ? ` · Programmata: ${new Date(item.scheduled_at).toLocaleString("it-IT")}`
                          : ""}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(item.created_at).toLocaleString("it-IT")} · {item.created_by_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setDettaglio(item)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {(item.stato === "bozza" || item.stato === "programmata" || item.stato === "fallita" || item.stato === "parziale") && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-600"
                          disabled={sendingId === item.id}
                          onClick={() => handleInvia(item.id)}
                        >
                          {sendingId === item.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {item.stato === "saltata_backlog" && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs gap-1 text-amber-800 btn-invia-ora"
                            aria-label="Invia ora"
                            disabled={sendingId === item.id}
                            onClick={() => setConfermaInvio(item)}
                          >
                            {sendingId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Invia ora
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs gap-1 btn-riprogramma"
                            aria-label="Riprogramma"
                            onClick={() => apriRiprogramma(item)}
                          >
                            <Clock className="w-3.5 h-3.5" />
                            Riprogramma
                          </Button>
                        </>
                      )}
                      {(item.stato === "bozza" || item.stato === "programmata" || item.stato === "saltata_backlog") && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          disabled={deletingId === item.id}
                          onClick={() => handleElimina(item.id)}
                        >
                          {deletingId === item.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Conferma "Invia ora" (comunicazione non inviata per arretrato) */}
      {confermaInvio && (
        <Dialog open={!!confermaInvio} onOpenChange={() => setConfermaInvio(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Inviare ora questa comunicazione?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              «{confermaInvio.oggetto || "(senza oggetto)"}» era programmata per il{" "}
              {confermaInvio.scheduled_at ? new Date(confermaInvio.scheduled_at).toLocaleString("it-IT") : "—"}{" "}
              e non è stata inviata. Verrà inviata subito a {(confermaInvio.destinatari || []).length} destinatario/i.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfermaInvio(null)}>Annulla</Button>
              <Button className="bg-green-600 hover:bg-green-700" data-testid="conferma-invia-ora" onClick={confermaInvioOra}>
                Conferma invio
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Riprogramma (comunicazione non inviata per arretrato) */}
      {riprogramma && (
        <Dialog open={!!riprogramma} onOpenChange={() => !riprogrammando && setRiprogramma(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Riprogramma l'invio</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="riprogramma-data" className="text-xs">Data</Label>
                <Input id="riprogramma-data" type="date" value={riprogramma.data}
                  onChange={(e) => setRiprogramma((r) => ({ ...r, data: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="riprogramma-ora" className="text-xs">Ora</Label>
                <Input id="riprogramma-ora" type="time" value={riprogramma.ora}
                  onChange={(e) => setRiprogramma((r) => ({ ...r, ora: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" disabled={riprogrammando} onClick={() => setRiprogramma(null)}>Annulla</Button>
              <Button data-testid="salva-riprogramma" disabled={riprogrammando} onClick={salvaRiprogramma}>
                {riprogrammando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Riprogramma"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog dettaglio */}
      {dettaglio && (
        <Dialog open={!!dettaglio} onOpenChange={() => setDettaglio(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{dettaglio.oggetto || "(senza oggetto)"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Messaggio</p>
                <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap max-h-40 overflow-auto">
                  {dettaglio.messaggio}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">
                  Destinatari ({(dettaglio.destinatari || []).length})
                </p>
                <div className="space-y-1 max-h-48 overflow-auto">
                  {(dettaglio.destinatari || []).map((d, i) => {
                    const ok = d.stato_invio === "sent";
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                          ok ? "bg-green-50" : d.stato_invio === "failed" ? "bg-red-50" : "bg-slate-50"
                        }`}
                      >
                        {ok ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                        ) : d.stato_invio === "failed" ? (
                          <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        ) : (
                          <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        )}
                        <div className="flex-1">
                          <span className="font-medium">{d.familiare_nome}</span>
                          <span className="text-slate-500 ml-1">({d.ospite_nome})</span>
                          {d.email && <span className="text-slate-400 ml-1">· {d.email}</span>}
                          {d.error && <span className="text-red-500 block">{d.error}</span>}
                        </div>
                        {d.sent_at && (
                          <span className="text-slate-400 shrink-0">
                            {new Date(d.sent_at).toLocaleString("it-IT")}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDettaglio(null)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Tab: Gestione Modelli
// ─────────────────────────────────────────────────────────────
function GestioneModelli({ authHeader }) {
  const { activeStruttura } = useAuth();
  const [modelli, setModelli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nome: "", descrizione: "", oggetto: "", messaggio: "" });
  const authorization = authHeader?.Authorization;

  const loadModelli = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/comunicazioni/modelli`, {
        headers: { Authorization: authorization },
      });
      setModelli(data);
    } catch {
      toast.error("Errore nel caricamento dei modelli");
    } finally {
      setLoading(false);
    }
  }, [authorization]);

  useEffect(() => { loadModelli(); }, [activeStruttura?.id, loadModelli]);

  function apriCrea() {
    setEditing(null);
    setForm({ nome: "", descrizione: "", oggetto: "", messaggio: "" });
    setDialogOpen(true);
  }

  function apriModifica(m) {
    setEditing(m);
    setForm({ nome: m.nome, descrizione: m.descrizione || "", oggetto: m.oggetto || "", messaggio: m.messaggio });
    setDialogOpen(true);
  }

  async function handleSalva() {
    if (!form.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    if (!form.messaggio.trim()) { toast.error("Messaggio obbligatorio"); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/comunicazioni/modelli/${editing.id}`, form, { headers: authHeader });
        toast.success("Modello aggiornato");
      } else {
        await axios.post(`${API_URL}/api/comunicazioni/modelli`, form, { headers: authHeader });
        toast.success("Modello creato");
      }
      setDialogOpen(false);
      loadModelli();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  }

  async function handleElimina(id) {
    try {
      await axios.delete(`${API_URL}/api/comunicazioni/modelli/${id}`, { headers: authHeader });
      toast.success("Modello eliminato");
      loadModelli();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          Crea e gestisci i modelli di messaggio per la tua struttura
        </p>
        <Button className="bg-green-600 hover:bg-green-700 text-white gap-2" onClick={apriCrea}>
          <Plus className="w-4 h-4" /> Nuovo modello
        </Button>
      </div>

      {modelli.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium text-slate-500">Nessun modello</p>
          <p className="text-sm">Crea il tuo primo modello di comunicazione</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {modelli.map((m) => (
            <Card key={m.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-2 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">{m.nome}</p>
                    {m.descrizione && (
                      <p className="text-xs text-slate-500 mt-0.5">{m.descrizione}</p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreVertical className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => apriModifica(m)}>
                        <Edit className="w-4 h-4 mr-2" /> Modifica
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleElimina(m.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Elimina
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {m.oggetto && (
                  <p className="text-xs font-medium text-slate-600 mb-1">Oggetto: {m.oggetto}</p>
                )}
                <p className="text-xs text-slate-500 line-clamp-3">{m.messaggio}</p>
                {m.ultimo_utilizzo && (
                  <p className="text-[10px] text-slate-400 mt-2">
                    Ultimo utilizzo: {new Date(m.ultimo_utilizzo).toLocaleDateString("it-IT")}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica modello" : "Nuovo modello"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                placeholder="Es. Aggiornamento attività quotidiane"
              />
            </div>
            <div>
              <Label className="text-xs">Descrizione</Label>
              <Input
                value={form.descrizione}
                onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))}
                placeholder="Descrizione opzionale"
              />
            </div>
            <div>
              <Label className="text-xs">Oggetto email</Label>
              <Input
                value={form.oggetto}
                onChange={(e) => setForm((f) => ({ ...f, oggetto: e.target.value }))}
                placeholder="Es. Aggiornamento su {nome_ospite}"
              />
            </div>
            <div>
              <Label className="text-xs">Messaggio *</Label>
              <Textarea
                value={form.messaggio}
                onChange={(e) => setForm((f) => ({ ...f, messaggio: e.target.value }))}
                placeholder={`Usa placeholder: {nome_ospite}, {nome_familiare}, {relazione}, {struttura}, {data}, {firma}`}
                className="min-h-[120px] resize-none"
                maxLength={10000}
              />
              <p className="text-xs text-slate-400 mt-1">
                Placeholder disponibili: {"{nome_ospite}"}, {"{nome_familiare}"}, {"{relazione}"}, {"{struttura}"}, {"{data}"}, {"{firma}"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleSalva}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Salva modifiche" : "Crea modello"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Tab: Impostazioni
// ─────────────────────────────────────────────────────────────
function ImpostazioniComunicazioni({ authHeader }) {
  const { activeStruttura } = useAuth();
  const [firma, setFirma] = useState("");
  const [mittente, setMittente] = useState("");
  const [firmaAuto, setFirmaAuto] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/comunicazioni/impostazioni`, { headers: authHeader });
        setFirma(data.firma_default || "");
        setMittente(data.mittente_nome || "");
        setFirmaAuto(data.firma_automatica || false);
      } catch {
        // silente
      } finally {
        setLoading(false);
      }
    })();
  }, [authHeader, activeStruttura?.id]);

  async function handleSalva() {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/comunicazioni/impostazioni`,
        { firma_default: firma, mittente_nome: mittente, firma_automatica: firmaAuto },
        { headers: authHeader }
      );
      toast.success("Impostazioni salvate");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Firma email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">
              Firma predefinita (mostrata in calce alle email)
            </Label>
            <Textarea
              value={firma}
              onChange={(e) => setFirma(e.target.value)}
              placeholder="Es. Cordiali saluti,&#10;Residenza Althea&#10;Tel. 02 1234567"
              className="min-h-[100px] resize-none"
            />
            <p className="text-xs text-slate-400 mt-1">
              Puoi usare il placeholder <span className="font-mono">{"{firma}"}</span> nei messaggi per inserire automaticamente questa firma.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="firma-auto"
              checked={firmaAuto}
              onCheckedChange={setFirmaAuto}
            />
            <Label htmlFor="firma-auto" className="text-sm cursor-pointer">
              Inserisci automaticamente la firma in tutti i messaggi
            </Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Mittente</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">
              Nome mittente (mostrato come "Da:" nel client email)
            </Label>
            <Input
              value={mittente}
              onChange={(e) => setMittente(e.target.value)}
              placeholder="Es. Residenza Althea"
            />
            <p className="text-xs text-slate-400 mt-1">
              L'indirizzo email mittente è configurato dal sistema (noreply@affidya.it).
            </p>
          </div>
        </CardContent>
      </Card>

      <Button
        className="bg-green-600 hover:bg-green-700 text-white gap-2"
        onClick={handleSalva}
        disabled={saving}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Salva impostazioni
      </Button>
    </div>
  );
}
